import { TrashIcon, PlusIcon, TagIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import ModalDelete from '../../components/modal-delete/modal-delete';
import Notification from '../../components/notification/notification';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import tagApi from '../../services/tagApi';
import TagPreview from './components/TagPreview';
import TagBadge from './components/TagBadge';
import ColorPicker from './components/ColorPicker';
import {
  IconBadge,
  PageHeader,
  TWButton,
  TWInput,
  TWModal,
  TWDataTable,
} from '../../components/ui';
import { headerCls, EditAction } from '../../data/columns';

const Tags = () => {
  const [tagList, setTagList] = useState([]);
  const [deletedTag, setDeletedTag] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [form, setForm] = useState({
    id: '',
    text: '',
    textColor: 'white',
    color: 'blue',
  });

  const [dialogs, setDialogs] = useState({
    upsert: false,
    delete: false,
  });

  const [errors, setErrors] = useState({
    text: false,
    textColor: false,
    color: false,
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });

  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const getTagList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setTagList([]);
    try {
      const query = {
        pageNumber: pagination.pageNumber,
        pageSize: pagination.pageSize,
      };
      const list = await tagApi.getTags(query);
      if (list.data.length > 0) {
        setTagList(list.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickAdd = () => {
    setForm({
      id: '',
      text: '',
      textColor: 'white',
      color: 'blue',
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onClickEdit = (val) => {
    setForm({
      id: val.id,
      text: val.text,
      textColor: val.textColor,
      color: val.color,
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (!form.text || !form.textColor || !form.color) {
      setErrors((prev) => ({
        ...prev,
        text: !form.text ? true : false,
        textColor: !form.textColor ? true : false,
        color: !form.color ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        text: false,
        textColor: false,
        color: false,
      }));
    }

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));

      const body = { ...form };
      if (form.id) {
        await tagApi.updateTag(body);
      } else {
        await tagApi.createTag(body);
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      await getTagList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({
      ...prev,
      text: false,
      textColor: false,
      color: false,
    }));
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (tag) => {
    setDeletedTag({ id: tag.id, name: tag.text });
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await tagApi.deleteTag(deletedTag.id);
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getTagList();
    }
  };

  useEffect(() => {
    getTagList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber]);

  const columns = [
    {
      title: <span className={headerCls}>Text</span>,
      key: 'text',
      render: (_, tag) => (
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {tag.text}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Badge</span>,
      key: 'badge',
      render: (_, tag) => (
        <TagBadge
          text={tag.text}
          textColor={tag.textColor}
          color={tag.color}
          size="sm"
        />
      ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, tag) => (
        <EditAction onClick={() => onClickEdit(tag)} label="Edit tag" />
      ),
    },
  ];

  const TagsSkeleton = () => (
    <div className="reveal" aria-hidden="true">
      <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-500">
        <thead>
          <tr>
            <th className="py-3.5 pr-3 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Text
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Badge
            </th>
            <th className="py-3.5 px-4 text-right">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-slate-500">
          {[...Array(5)].map((_, idx) => (
            <tr key={idx}>
              <td className="whitespace-nowrap py-4 pr-3">
                <SkeletonBlock className="h-4 w-32" />
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-6 w-20 !rounded-full" />
              </td>
              <td className="whitespace-nowrap px-4 py-4 text-right">
                <SkeletonBlock className="ml-auto h-8 w-8 !rounded-full" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Tags"
          description="Build colorful labels to highlight and organize items across your catalog."
          actions={
            <TWButton
              variant="primary"
              icon={PlusIcon}
              label="Add Tag"
              onClick={onClickAdd}
            />
          }
        />

        {loading.listLoading ? (
          <TagsSkeleton />
        ) : tagList.length > 0 ? (
          <div className="reveal">
            <TWDataTable
              columns={columns}
              data={tagList}
              rowKey="id"
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} tags`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <div className="flex h-16 w-16 items-center justify-center rounded-md bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white shadow-lg">
              <TagIcon className="h-8 w-8" />
            </div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              No tags yet
            </p>
            <p className="max-w-sm text-center text-xs text-slate-500 dark:text-slate-400">
              Create your first tag to start labeling items with custom colors.
            </p>
            <TWButton
              variant="primary"
              icon={PlusIcon}
              label="Add Tag"
              onClick={onClickAdd}
              className="mt-2"
            />
          </div>
        )}
      </div>

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        size="2xl"
        dismissible={false}
        bodyClassName="p-0"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50/60 px-6 py-5 dark:border-slate-700 dark:bg-slate-900/40">
          <div className="flex items-center gap-3">
            <IconBadge icon={TagIcon} tone="violet" size="md" />
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {form.id ? 'Edit Tag' : 'Create Tag'}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {form.id
                  ? 'Update the styling and label for this tag.'
                  : 'Design a new tag with your own text and colors.'}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 px-6 py-6">
          <TWInput
            label="Tag Text"
            required
            value={form.text}
            onChange={(e) => onFormChange(e.target.value, 'text')}
            placeholder="e.g. New, Popular, Sale"
            error={errors.text ? 'Tag text can not be empty' : ''}
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <ColorPicker
                label="Text Color*"
                value={form.textColor}
                onChange={(value) => onFormChange(value, 'textColor')}
                placeholder="white"
                presetColors={[
                  { color: '#ffffff', name: 'white' },
                  { color: '#000000', name: 'black' },
                  { color: '#374151', name: 'gray' },
                  { color: '#6b7280', name: 'darkgray' },
                  { color: '#9ca3af', name: 'lightgray' },
                  { color: '#d1d5db', name: 'silver' },
                  { color: '#f3f4f6', name: 'whitesmoke' },
                  { color: '#1f2937', name: 'charcoal' },
                  { color: '#111827', name: 'darkslategray' },
                  { color: '#030712', name: 'almostblack' },
                ]}
              />
              <div className="min-h-[1rem] pt-1 text-xs">
                {errors.textColor && (
                  <p className="text-rose-600 dark:text-rose-400">
                    Text color can not be empty
                  </p>
                )}
              </div>
            </div>

            <div>
              <ColorPicker
                label="Background Color*"
                value={form.color}
                onChange={(value) => onFormChange(value, 'color')}
                placeholder="blue"
                presetColors={[
                  { color: '#3b82f6', name: 'blue' },
                  { color: '#ef4444', name: 'red' },
                  { color: '#10b981', name: 'green' },
                  { color: '#f59e0b', name: 'orange' },
                  { color: '#8b5cf6', name: 'purple' },
                  { color: '#06b6d4', name: 'cyan' },
                  { color: '#ffff00', name: 'yellow' },
                  { color: '#ec4899', name: 'pink' },
                  { color: '#00ffff', name: 'aqua' },
                  { color: '#6b7280', name: 'gray' },
                ]}
              />
              <div className="min-h-[1rem] pt-1 text-xs">
                {errors.color && (
                  <p className="text-rose-600 dark:text-rose-400">
                    Background color can not be empty
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/60 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
              Live Preview
            </p>
            <TagPreview
              text={form.text}
              textColor={form.textColor}
              color={form.color}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
          <div>
            {form.id && (
              <TWButton
                variant="danger"
                icon={TrashIcon}
                disabled={loading.deleteLoading}
                onClick={() => onClickDelete(form)}
                aria-label="Delete tag"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <TWButton
              variant="default"
              label="Cancel"
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="primary"
              label={
                loading.upsertLoading
                  ? 'Saving…'
                  : form.id
                    ? 'Update Tag'
                    : 'Create Tag'
              }
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Tag"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedTag}
      />
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default Tags;
